import React, { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './PolicyDetailPage.css';

interface Policy {
  id: string;
  name: string;
  type: 'Network' | 'File' | 'Process' | 'System' | 'Capability';
  status: 'Active' | 'Inactive' | 'Reviewing';
  effectiveness: number;
  namespace: string;
  createdDate: string;
  lastModified: string;
  targetPods: number;
  violations: number;
  recentViolationSource?: string; // 최근 위반 출처 (프로세스/사용자)
}

const PolicyDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const policyData: Policy[] = useMemo(() => ([
    { id: 'pol-1', name: 'Deny Root Access', type: 'Process', status: 'Active', effectiveness: 95, namespace: 'production', createdDate: '2024-01-10', lastModified: '2024-01-15', targetPods: 45, violations: 2, recentViolationSource: '/bin/bash (uid: 0)' },
    { id: 'pol-2', name: 'Block External DNS', type: 'Network', status: 'Active', effectiveness: 88, namespace: 'production', createdDate: '2024-01-08', lastModified: '2024-01-14', targetPods: 32, violations: 5, recentViolationSource: 'curl (uid: 1000)' },
    { id: 'pol-3', name: 'Restrict File Access', type: 'File', status: 'Active', effectiveness: 92, namespace: 'default', createdDate: '2024-01-05', lastModified: '2024-01-12', targetPods: 28, violations: 1, recentViolationSource: 'python (uid: 1000)' },
    { id: 'pol-4', name: 'Limit Capabilities', type: 'Capability', status: 'Active', effectiveness: 85, namespace: 'production', createdDate: '2024-01-03', lastModified: '2024-01-11', targetPods: 50, violations: 8, recentViolationSource: 'java (uid: 999)' },
    { id: 'pol-5', name: 'System Call Monitoring', type: 'System', status: 'Reviewing', effectiveness: 78, namespace: 'staging', createdDate: '2024-01-12', lastModified: '2024-01-13', targetPods: 15, violations: 12, recentViolationSource: 'node (uid: 1000)' },
    { id: 'pol-6', name: 'Network Egress Control', type: 'Network', status: 'Active', effectiveness: 91, namespace: 'production', createdDate: '2024-01-09', lastModified: '2024-01-14', targetPods: 38, violations: 3, recentViolationSource: 'redis-cli (uid: 1001)' },
    { id: 'pol-7', name: 'Container Image Verification', type: 'System', status: 'Inactive', effectiveness: 0, namespace: 'default', createdDate: '2024-01-01', lastModified: '2024-01-02', targetPods: 0, violations: 0, recentViolationSource: undefined },
    { id: 'pol-8', name: 'Privileged Container Prevention', type: 'Capability', status: 'Active', effectiveness: 93, namespace: 'production', createdDate: '2024-01-07', lastModified: '2024-01-15', targetPods: 42, violations: 1, recentViolationSource: 'init (uid: 0)' },
  ]), []);

  const filteredPolicies = useMemo(() => {
    return policyData.filter(policy => {
      const typeMatch = filterType === 'all' || policy.type === filterType;
      const statusMatch = filterStatus === 'all' || policy.status === filterStatus;
      return typeMatch && statusMatch;
    });
  }, [filterType, filterStatus, policyData]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Network': return '#3b82f6';
      case 'File': return '#8b5cf6';
      case 'Process': return '#ec4899';
      case 'System': return '#f59e0b';
      case 'Capability': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#22c55e';
      case 'Inactive': return '#9ca3af';
      case 'Reviewing': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getEffectivenessColor = (effectiveness: number) => {
    if (effectiveness >= 90) return '#22c55e';
    if (effectiveness >= 80) return '#84cc16';
    if (effectiveness >= 70) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="detail-page policy-analysis">
      <div className="detail-header">
        <h1>{t('policyAnalysis.title')}</h1>
        <p>{t('policyAnalysis.description')}</p>
      </div>

      {/* 필터 섹션 */}
      <section className="filter-section">
        <div className="filter-group">
          <label>{t('policyAnalysis.filters.policyType')}</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">{t('policyAnalysis.filters.all')}</option>
            <option value="Network">Network</option>
            <option value="File">File</option>
            <option value="Process">Process</option>
            <option value="System">System</option>
            <option value="Capability">Capability</option>
          </select>
        </div>
        <div className="filter-group">
          <label>{t('policyAnalysis.filters.status')}</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">{t('policyAnalysis.filters.all')}</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Reviewing">Reviewing</option>
          </select>
        </div>
        <div className="filter-stat">
          <span>{t('policyAnalysis.filters.searchResults')}: <strong>{filteredPolicies.length}</strong></span>
        </div>
      </section>

      {/* 테이블 및 상세 정보 */}
      <div className="content-layout">
        {/* 데이터 테이블 */}
        <section className="table-section">
          <div className="table-header">
            <div>{t('policyAnalysis.columns.policyName')}</div>
            <div>{t('policyAnalysis.columns.type')}</div>
            <div>{t('policyAnalysis.columns.status')}</div>
            <div>{t('policyAnalysis.columns.effectiveness')}</div>
            <div>{t('policyAnalysis.columns.targetPods')}</div>
            <div>{t('policyAnalysis.columns.violations')}</div>
          </div>
          <div className="table-body">
            {filteredPolicies.map((policy) => (
              <div
                key={policy.id}
                className={`table-row ${selectedPolicy?.id === policy.id ? 'active' : ''}`}
                onClick={() => setSelectedPolicy(policy)}
              >
                <div>{policy.name}</div>
                <div>
                  <span className="type-badge" style={{backgroundColor: `${getTypeColor(policy.type)}20`, color: getTypeColor(policy.type)}}>
                    {policy.type}
                  </span>
                </div>
                <div>
                  <span className="status-badge" style={{backgroundColor: `${getStatusColor(policy.status)}20`, color: getStatusColor(policy.status)}}>
                    {policy.status}
                  </span>
                </div>
                <div>
                  <div className="effectiveness-bar">
                    <div className="effectiveness-fill" style={{width: `${policy.effectiveness}%`, backgroundColor: getEffectivenessColor(policy.effectiveness)}} />
                  </div>
                  <span style={{fontSize: '11px', color: getEffectivenessColor(policy.effectiveness)}}>{policy.effectiveness}%</span>
                </div>
                <div>{policy.targetPods}</div>
                <div>
                  <span style={{color: policy.violations > 0 ? '#ef4444' : '#22c55e'}}>{policy.violations}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 상세 정보 */}
        <section className="detail-section">
          {selectedPolicy ? (
            <div className="detail-card">
              <div className="detail-title">정책 상세</div>
              <div className="detail-content">
                <div className="detail-row">
                  <div className="detail-label">정책 ID</div>
                  <div className="detail-value">{selectedPolicy.id}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">정책명</div>
                  <div className="detail-value">{selectedPolicy.name}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">타입</div>
                  <div className="detail-value">
                    <span className="type-badge" style={{backgroundColor: `${getTypeColor(selectedPolicy.type)}20`, color: getTypeColor(selectedPolicy.type)}}>
                      {selectedPolicy.type}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">상태</div>
                  <div className="detail-value">
                    <span className="status-badge" style={{backgroundColor: `${getStatusColor(selectedPolicy.status)}20`, color: getStatusColor(selectedPolicy.status)}}>
                      {selectedPolicy.status}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">효과성</div>
                  <div className="detail-value">
                    <div className="effectiveness-bar" style={{marginBottom: '4px'}}>
                      <div className="effectiveness-fill" style={{width: `${selectedPolicy.effectiveness}%`, backgroundColor: getEffectivenessColor(selectedPolicy.effectiveness)}} />
                    </div>
                    <span style={{color: getEffectivenessColor(selectedPolicy.effectiveness)}}>{selectedPolicy.effectiveness}%</span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">네임스페이스</div>
                  <div className="detail-value">{selectedPolicy.namespace}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">생성 일자</div>
                  <div className="detail-value">{selectedPolicy.createdDate}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">마지막 수정</div>
                  <div className="detail-value">{selectedPolicy.lastModified}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">대상 Pod</div>
                  <div className="detail-value">{selectedPolicy.targetPods}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">위반 사항</div>
                  <div className="detail-value">
                    <span style={{color: selectedPolicy.violations > 0 ? '#ef4444' : '#22c55e', fontWeight: 'bold'}}>
                      {selectedPolicy.violations}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="detail-card empty-detail">
              <div className="empty-state-message">
                <p>📋 목록에서 정책을 선택하세요.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PolicyDetailPage;
